import Vue from 'vue'
import {
  ACCOUNT_SHARING_LOGIN_NOTICE_BANNER_SELECTOR,
  ACCOUNT_SHARING_LOGIN_NOTICE_CTA_HASH,
  USETIFUL_CLOSE_BUTTON_SELECTOR,
  USETIFUL_OPEN_URL_ACTION_SELECTOR,
  getUserId
} from '~/utils/accountSharingLoginNotice'
import {
  bannerHasAccountSharingMarker,
  createAccountSharingLoginNoticeTracker,
  elementContainsAccountSharingMarker
} from '~/utils/accountSharingLoginNoticeTracking'

let installation = null

function openChat(app) {
  const chat = (app && app.$chat) || Vue.prototype.$chat

  if (!chat) return

  if (typeof chat.open === 'function') {
    chat.open()
  }
}

function initializeUsetifulTags(app, user) {
  const userId = getUserId(user)
  const email = user && user.email

  if (!userId || !email) return false

  if (
    app &&
    app.$usetiful &&
    typeof app.$usetiful.usetifulTags === 'function'
  ) {
    app.$usetiful.usetifulTags(String(userId), 'USERS', email)
    return true
  }

  return false
}

export default ({ app, store }) => {
  if (!process.client || typeof window === 'undefined') return
  if (installation) return

  const tracker = createAccountSharingLoginNoticeTracker({
    capture(eventName, properties) {
      if (app.$posthog && typeof app.$posthog.capture === 'function') {
        app.$posthog.capture(eventName, properties)
      }
    }
  })
  const stopWatchers = []

  /** @type {string | null} */
  let lastRefreshedUserId = null
  /** @type {string | null} */
  let lastTaggedUsetifulUserKey = null

  const refresh = user => {
    const userId = String(getUserId(user) || '')
    const email = (user && user.email) || ''
    const usetifulUserKey = userId && email ? `${userId}:${email}` : null

    if (
      usetifulUserKey &&
      usetifulUserKey !== lastTaggedUsetifulUserKey &&
      initializeUsetifulTags(app, user)
    ) {
      lastTaggedUsetifulUserKey = usetifulUserKey
    }

    if (userId !== lastRefreshedUserId) {
      if (lastRefreshedUserId !== null) {
        tracker.updateTrackingContext('ineligible', null)
      }
      lastRefreshedUserId = userId
      store.dispatch('accountSharingLoginNotice/refresh')
    }
  }

  const syncTrackingContext = () => {
    const trackingState =
      store.state && store.state.accountSharingLoginNotice
        ? store.state.accountSharingLoginNotice
        : {}
    tracker.updateTrackingContext(
      trackingState.trackingStatus,
      trackingState.riskLevel
    )
  }

  const handleClick = event => {
    const target = event && event.target
    if (!target || typeof target.closest !== 'function') return

    const banner = target.closest(ACCOUNT_SHARING_LOGIN_NOTICE_BANNER_SELECTOR)
    if (!banner) return

    const openUrlAction = target.closest(USETIFUL_OPEN_URL_ACTION_SELECTOR)
    if (
      openUrlAction &&
      banner.contains(openUrlAction) &&
      elementContainsAccountSharingMarker(openUrlAction)
    ) {
      tracker.trackTalkToUs()
      event.preventDefault()
      event.stopPropagation()
      clearTalkToUsHash()
      openChat(app)
      return
    }

    const closeButton = target.closest(USETIFUL_CLOSE_BUTTON_SELECTOR)
    if (
      closeButton &&
      banner.contains(closeButton) &&
      bannerHasAccountSharingMarker(banner)
    ) {
      tracker.trackClose()
      store.dispatch('accountSharingLoginNotice/dismiss')
    }
  }

  document.addEventListener('click', handleClick, true)

  if (store && typeof store.watch === 'function') {
    stopWatchers.push(
      store.watch(state => state.auth && state.auth.user, refresh),
      store.watch(
        state =>
          state.accountSharingLoginNotice &&
          state.accountSharingLoginNotice.trackingStatus,
        syncTrackingContext
      )
    )
  }

  syncTrackingContext()
  refresh(app && app.$auth && app.$auth.user)
  tracker.start()

  const destroy = () => {
    document.removeEventListener('click', handleClick, true)
    stopWatchers.forEach(stop => stop())
    tracker.destroy()
    if (installation && installation.destroy === destroy) installation = null
  }

  installation = { destroy }

  const rootApp = app.router && app.router.app
  if (rootApp && typeof rootApp.$once === 'function') {
    rootApp.$once('hook:beforeDestroy', destroy)
  }
}

export function destroyAccountSharingLoginNoticePlugin() {
  if (installation) installation.destroy()
}

function clearTalkToUsHash() {
  if (window.location.hash !== ACCOUNT_SHARING_LOGIN_NOTICE_CTA_HASH) return

  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${window.location.search}`
  )
}
